import { Injectable, inject, EnvironmentInjector, runInInjectionContext, Inject } from '@angular/core';
import { Database, DataSnapshot, onValue, query, ref } from '@angular/fire/database';
import { Observable } from 'rxjs';
import {
  MessagingCounts,
  MessagingCurrentContext,
  MessagingNotification,
  MessagingParticipantState,
  MessagingThread,
  MessagingThreadSummary
} from './messaging.models';
import { messagingFirebaseRootToken } from '../app.config';

@Injectable({ providedIn: 'root' })
export class MessagingRealtimeService {
  private readonly db = inject(Database);
  private readonly injector = inject(EnvironmentInjector);
  private readonly messagingRoot = inject(messagingFirebaseRootToken);

  watchThread(threadId: string, context: MessagingCurrentContext): Observable<MessagingThread | null> {
    return this.watchSinglePath<MessagingThread>(
      `organizations/${context.organizationId}/threads/${threadId}`
    );
  }

  watchThreadParticipantState(threadId: string, participantId: string, context: MessagingCurrentContext): Observable<MessagingParticipantState | null> {
    return this.watchSinglePath<MessagingParticipantState>(
      `organizations/${context.organizationId}/threads/${threadId}/participants/${participantId}`
    );
  }

  watchThreadIndex(context: MessagingCurrentContext): Observable<MessagingThreadSummary[]> {
    console.log('Watching thread index for context:', `organizations/${context.organizationId}/users/${context.participantId}/threadIndex`);
    return this.watchCollectionPath<MessagingThreadSummary>(
      `organizations/${context.organizationId}/users/${context.participantId}/threadIndex`
    );
  }

  watchNotifications(context: MessagingCurrentContext): Observable<MessagingNotification[]> {
    console.log('Watching notifications for context:', `organizations/${context.organizationId}/users/${context.participantId}/notifications`); 
    return this.watchCollectionPath<MessagingNotification>(
      `organizations/${context.organizationId}/users/${context.participantId}/notifications`
    );
  }

  watchCounts(context: MessagingCurrentContext): Observable<MessagingCounts | null> {
    console.log('Watching counts for context:', `organizations/${context.organizationId}/users/${context.participantId}/counts`);
    return this.watchSinglePath<MessagingCounts>(
      `organizations/${context.organizationId}/users/${context.participantId}/counts`
    );
  }

  private watchSinglePath<T>(path: string): Observable<T | null> {
    return new Observable<T | null>((observer) => {
      console.log('[RTDB] watchSinglePath invoked', path);
      try {
        return runInInjectionContext(this.injector, () => {
          const fullPath = this.messagingRoot ? `${this.messagingRoot}/${path}` : path;
          console.log('[RTDB] subscribe', fullPath);
          const pathRef = ref(this.db, fullPath);
          const unsubscribe = onValue(
            query(pathRef),
            (snapshot: DataSnapshot) => {
              try {
                console.log('[RTDB] onValue snapshot', path, snapshot.exists(), snapshot.val());
                observer.next(snapshot.exists() ? (snapshot.val() as T) : null);
              } catch (e) {
                console.error('[RTDB] observer.next error', path, e);
              }
            },
            (error: Error) => {
              console.error('[RTDB] onValue error', path, error);
              observer.error(error);
            }
          );

          return () => {
            try {
              runInInjectionContext(this.injector, () => {
                try {
                  unsubscribe();
                  console.log('[RTDB] unsubscribe', fullPath);
                } catch (ue) {
                  console.warn('[RTDB] unsubscribe failed, falling back', path, ue);
                  try { unsubscribe(); } catch (e) { /* swallow */ }
                }
              });
            } catch (e) {
              try { unsubscribe(); } catch (err) { console.warn('[RTDB] teardown unsubscribe failed', path, err); }
            }
          };
        });
      } catch (e) {
        console.error('[RTDB] runInInjectionContext subscribe failed', path, e);
        return () => { /* noop */ };
      }
    });
  }

  private watchCollectionPath<T>(path: string): Observable<T[]> {
    return new Observable<T[]>((observer) => {
          try {
            return runInInjectionContext(this.injector, () => {
              const fullPath = this.messagingRoot ? `${this.messagingRoot}/${path}` : path;
              console.log('[RTDB] subscribe collection', fullPath);
              const pathRef = ref(this.db, fullPath);
          const unsubscribe = onValue(
            query(pathRef),
            (snapshot: DataSnapshot) => {
              try {
                console.log('[RTDB] onValue snapshot', path, snapshot.exists(), snapshot.val());
                if (!snapshot.exists()) {
                  observer.next([]);
                  return;
                }

                const raw = snapshot.val() as Record<string, T>;
                observer.next(Object.values(raw ?? {}));
              } catch (e) {
                console.error('[RTDB] observer.next(collection) error', path, e);
              }
            },
            (error: Error) => {
              console.error('[RTDB] onValue(collection) error', path, error);
              observer.error(error);
            }
          );

          return () => {
            try {
              runInInjectionContext(this.injector, () => {
                try {
                  unsubscribe();
                  console.log('[RTDB] unsubscribe collection', fullPath);
                } catch (ue) {
                  console.warn('[RTDB] unsubscribe collection failed, falling back', path, ue);
                  try { unsubscribe(); } catch (e) { /* swallow */ }
                }
              });
            } catch (e) {
              try { unsubscribe(); } catch (err) { console.warn('[RTDB] teardown unsubscribe collection failed', path, err); }
            }
          };
        });
      } catch (e) {
        console.error('[RTDB] runInInjectionContext subscribe collection failed', path, e);
        return () => { /* noop */ };
      }
    });
  }
}

